<template>
  <div>
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" class="pa-2" v-if="loaded">

      <!-- Name + total time + advanced toggle header -->
      <v-card elevation="0" class="mb-2">
        <v-card-text class="pb-1">
          <v-text-field outlined dense v-model="timerData.name" :label="$t('message.name')"></v-text-field>

          <div class="d-flex align-center">
            <span class="text-subtitle-1">
              <b>{{ $t('message.total_time') }}:</b> {{ totalTime }}
            </span>
            <v-spacer></v-spacer>
            <span class="clickable text-subtitle-2" @click="toggleAdvanced()">
              <v-icon :color="timerData.advanced ? 'primary' : ''" class="mr-1">
                {{ timerData.advanced ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
              </v-icon>
              <span :class="timerData.advanced ? 'primary--text' : ''">
                {{ $t('message.advanced_options') }}
              </span>
            </span>
          </div>
        </v-card-text>
      </v-card>

      <!-- ── Simple mode (single interval) ───────────────────────────────── -->
      <v-card v-if="!timerData.advanced" elevation="0" outlined class="pa-3 mb-3">
        <v-row dense>
          <v-col cols="4">
            <v-text-field @change="calculateTotal" outlined dense hide-details v-model="timerData.intervals[0].rounds"
              :label="$t('message.rounds')" :rules="numberRules"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field @change="calculateTotal" outlined dense hide-details v-mask="timeMask" color="success"
              v-model="timerData.intervals[0].active" :label="$t('message.active')"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field @change="calculateTotal" outlined dense hide-details v-mask="timeMask" color="orange"
              v-model="timerData.intervals[0].rest" :label="$t('message.rest')"></v-text-field>
          </v-col>
        </v-row>

      </v-card>

      <!-- ── Advanced mode (multiple intervals) ─────────────────────────── -->
      <v-card v-else elevation="0" outlined class="pa-2 mb-3">
        <div class="px-1 pb-2">
          <v-switch dense hide-details v-model="timerData.cla" :label="$t('message.constraint_led_approach')"
            :disabled="timerData.cla && hasActiveIntros" class="mt-0"></v-switch>
          <div v-if="timerData.cla" class="text-caption mt-1 mb-1">{{ $t('message.constraint_led_approach_explained') }}</div>
          <v-divider class="mt-2"></v-divider>
        </div>
        <div v-for="(interval, index) in timerData.intervals" :key="'set' + index">
          <v-card-actions class="pa-0 pb-1">
            <span class="text-subtitle-2">{{ $t('message.interval') }} {{ index + 1 }}</span>
            <v-spacer></v-spacer>
            <v-btn @click="removeInterval(index)" small icon color="error">
              <v-icon size="18">mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-card-actions>

          <v-text-field outlined dense v-model="interval.name"
            :label="$t('message.name') + ' (' + $t('message.optional') + ')'"></v-text-field>

          <v-row dense>
            <v-col cols="4">
              <v-text-field @change="calculateTotal" outlined dense v-model="interval.rounds"
                :label="$t('message.rounds')"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field @change="calculateTotal" outlined dense v-mask="timeMask" color="success"
                v-model="interval.active" :label="$t('message.active')"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field @change="calculateTotal" outlined dense v-mask="timeMask" color="orange"
                v-model="interval.rest" :label="$t('message.rest')"></v-text-field>
            </v-col>
          </v-row>

          <template v-if="timerData.cla">
            <v-switch dense hide-details v-model="interval.intro.active" label="Intro" class="mb-2"></v-switch>
            <template v-if="interval.intro.active">
              <v-text-field outlined dense type="number" v-model.number="interval.intro.duration"
                label="Duration (s)"></v-text-field>
              <v-textarea outlined dense v-model="interval.intro.text" label="Instructions" rows="3"></v-textarea>
            </template>
          </template>

          <v-divider v-if="index + 1 < timerData.intervals.length" class="my-2"></v-divider>
        </div>

        <v-card-actions class="pa-0 pt-2">
          <v-spacer></v-spacer>
          <v-btn small @click="addNewInterval()" elevation="0" color="primary" outlined>
            <v-icon class="mr-1">mdi-plus-circle-outline</v-icon>{{ $t('message.interval') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- ── Action buttons ──────────────────────────────────────────────── -->
      <v-card-actions>
        <v-btn v-if="timerId == 'new'" small @click="createTimer()" elevation="0" color="primary" outlined>
          {{ $t('message.add_timer') }}
        </v-btn>
        <v-btn v-else small @click="updateTimer()" elevation="0" color="primary" outlined>
          {{ $t('message.update') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn small v-if="timerId != 'new'" @click="removeTimerDialog = true" elevation="0" color="error">
          {{ $t('message.delete') }}
        </v-btn>
      </v-card-actions>
    </v-sheet>

    <v-dialog v-model="removeTimerDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <h3>{{ $t("message.are_you_sure") }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-btn elevation="0" color="error" @click.stop="removeTimer()">{{ $t("message.delete") }}</v-btn>
          <v-btn elevation="0" @click.stop="removeTimerDialog = false">{{ $t("message.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TimerService from "@/services/TimerService";
import { mask } from "vue-the-mask";

export default {
  data() {
    return {
      timerId: this.$route.params.timerId,
      // Seeded with a full element so TypeScript infers the array element type.
      // 'loaded' gates the template; timerData is populated before loaded = true.
      loaded: false,
      timerData: {
        name: '',
        cla: false,
        advanced: false,
        intervals: [
          {
            name: null, rest: '00:20', active: '00:40', rounds: 1,
            intro: { active: false, text: '', duration: 10 }
          },
        ],
      },
      totalTime: null,
      removeTimerDialog: false,
      timeMask: '##:##',
      numberRules: [
        (v = '') => /^[\d]+$/.test(v) || this.$t("message.invalid_number"),
      ],
    };
  },
  directives: { mask },
  computed: {
    hasActiveIntros() {
      return this.timerData.intervals.some(
        (r) => r.intro && r.intro.active,
      );
    },
  },
  created() {
    if (this.timerId != 'new') {
      this.getTimer();
    } else {
      this.timerData = {
        name: '',
        cla: false,
        advanced: false,
        intervals: [this.newInterval()],
      };
      this.calculateTotal();
      this.loaded = true;
    }
  },
  methods: {
    newInterval() {
      return {
        name: null,
        rest: '00:20',
        active: '00:40',
        rounds: 1,
        intro: {
          active: false,
          text: '',
          duration: 10,
        },
      };
    },
    toggleAdvanced() {
      if (this.timerData.advanced) {
        this.timerData.advanced = false;
        this.timerData.intervals = [this.timerData.intervals[0] || this.newInterval()];
        this.calculateTotal();
      } else {
        this.timerData.advanced = true;
      }
    },
    addNewInterval() {
      this.timerData.intervals.push(this.newInterval());
    },
    removeInterval(i = 0) {
      this.timerData.intervals.splice(i, 1);
      this.calculateTotal();
    },
    removeTimer() {
      TimerService.removeTimer(this.timerId)
        .then((data = {}) => {
          if (data) {
            this.navigateTo({ name: "viewtimers" });
          }
        })
        .catch((err = null) => {
          if (err) this.showAlert("error", this.$t('message.oops_not_working'));
        });
    },
    updateTimer() {
      const data = this.prepareTimer();
      if (data == null) {
        this.showAlert("error", this.$t('message.fill_all_parameters'));
        return;
      }
      TimerService.updateTimer(this.timerId, data)
        .then((res = null) => {
          if (res) this.showAlert("success", this.$t('message.updated'));
        })
        .catch((err = null) => {
          if (err) this.showAlert("error", this.$t('message.oops_not_working'));
        });
    },
    createTimer() {
      const data = this.prepareTimer();
      if (data == null) {
        this.showAlert("error", this.$t('message.fill_all_parameters'));
        return;
      }
      TimerService.addTimer(data)
        .then((res = null) => {
          if (res) this.navigateTo({ name: "viewtimers" });
        })
        .catch((err = null) => {
          if (err) this.showAlert("error", this.$t('message.oops_not_working'));
        });
    },
    prepareTimer() {
      const timer = this.timerData;
      if (!timer || !timer.name || timer.name === "") return null;
      if (!timer.intervals || timer.intervals.length === 0) return null;

      return {
        name: timer.name,
        advanced: !!timer.advanced,
        cla: !!timer.cla,
        intervals: timer.intervals.map((r) => {
          const rest = this.timeToNumber(r.rest);
          const active = this.timeToNumber(r.active);
          return {
            name: r.name || null,
            rest,
            active,
            rounds: !isNaN(r.rounds) ? Number(r.rounds) : 1,
            intro: {
              active: !!(r.intro && r.intro.active),
              text: (r.intro && r.intro.text) || '',
              duration: (r.intro && r.intro.duration) || 10,
            },
          };
        }),
      };
    },
    getTimer() {
      TimerService.getTimer(this.timerId)
        .then((data = {
          timer: {
            name: '', cla: false, advanced: false,
            intervals: [{ name: null, rest: '00:00', active: '00:00', rounds: 1, intro: { active: false, text: '', duration: 10 } }]
          }
        }) => {
          if (data) {
            const timer = data.timer;
            if (timer) {
              for (const r of timer.intervals) {
                r.rest = this.numberToTime(r.rest);
                r.active = this.numberToTime(r.active);
                if (!r.intro) {
                  r.intro = { active: false, text: '', duration: 10 };
                }
              }
              this.timerData = timer;
              this.calculateTotal();
            }
          }
          this.loaded = true;
        })
        .catch((err = null) => {
          if (err) this.showAlert("error", this.$t('message.oops_not_working'));
          this.loaded = true;
        });
    },
    numberToTime(time = 0) {
      if (time && time > 0) {
        let minutes = String(Math.floor(time / 60)).padStart(2, '0');
        let seconds = String(time - Number(minutes) * 60).padStart(2, '0');
        return minutes + ":" + seconds;
      }
      return '00:00';
    },
    timeToNumber(text = '') {
      const str = String(text);
      if (str.includes(":")) {
        const [min, sec] = str.split(":");
        return Number(min) * 60 + Number(sec);
      }
      return 0;
    },
    calculateTotal() {
      let totalTime = 0;
      for (const r of this.timerData.intervals) {
        const intervalTime =
          this.timeToNumber(r.rest) + this.timeToNumber(r.active);
        totalTime += intervalTime * (Number(r.rounds) || 1);
      }
      this.totalTime = this.seconds2Minutes(totalTime);
    },
  },
};
</script>

<style scoped></style>
